import { Box, BoxProps } from "@chakra-ui/react";
import { px } from "@chakra-ui/utils";
import { Global } from "@emotion/react";
import clsx from "clsx";
import React, { useRef, useState } from "react";
import FocusLock from "react-focus-lock";
import { usePageContentContext } from "shared/components/layout/PageContentContext";
import { TableBoxContext } from "shared/components/table/box/TableBoxContext";
import { MaybernTableUIProvider } from "shared/components/table/maybern/MaybernTableUIContext";
import { useBoolean, useEventListener } from "usehooks-ts";

export interface TableBoxProps {
  isFullWidth?: boolean;
  isFullHeight?: boolean;
  isCollapsible?: boolean;
  boxProps?: BoxProps;
  expandedMinHeight?: string;
  isDefaultExpanded?: boolean;
  className?: string;
  isFillHeight?: boolean;
  isBulkEditor?: boolean;
}

export const TableBox: React.FC<React.PropsWithChildren<TableBoxProps>> = ({
  isFullWidth = false,
  isFullHeight: isFullHeightProp,
  isCollapsible: isCollapsibleProp,
  isDefaultExpanded = true,
  isFillHeight: fillHeight,
  isBulkEditor,
  expandedMinHeight,
  boxProps: { sx: boxPropsSx, ...boxProps } = {},
  className,
  children,
}) => {
  const bodyContext = usePageContentContext();
  const isFullHeight = isFullHeightProp ?? bodyContext.isFullHeight ?? false;

  const [title, setTitle] = useState<string>();
  const [isFullScreen, setFullScreen] = useState<boolean>(false);
  const { value: isExpanded, toggle: toggleExpanded } =
    useBoolean(isDefaultExpanded);

  const tableBoxRef = useRef<HTMLDivElement>(null);

  const headerNavHeight =
    document.querySelector(".mb-header-nav")?.clientHeight ?? 0;

  useEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      setFullScreen(false);
    }
  });

  const [collapseRef, setCollapseRef] = useState<HTMLElement | null>(null);
  const isCollapsible = Boolean(collapseRef) && isCollapsibleProp !== false;

  return (
    <TableBoxContext.Provider
      value={{
        collapseRef: setCollapseRef,
        isCollapsible,
        isExpanded,
        isFullHeight,
        isFullWidth,
        isFullScreen,
        setFullScreen,
        setTitle,
        title,
        toggleExpanded,
        tableBoxRef,
      }}
    >
      <Global
        styles={{
          ".mb-page-sidebar-toggle": {
            display: isFullScreen ? "none" : undefined,
          },
        }}
      />
      <MaybernTableUIProvider>
        <FocusLock disabled={!isFullScreen} className="mb-tablebox-focus-lock">
          <Box
            ref={tableBoxRef}
            boxShadow="sm"
            bg="white"
            maxWidth="unset"
            width="auto"
            height={fillHeight ? "100%" : undefined}
            display="flex"
            flexDirection="column"
            flexShrink={isExpanded ? undefined : 0}
            overflowX="hidden"
            className={clsx(
              {
                "mb-tablebox": true,
                "mb-tablebox-fullscreen": isFullScreen,
                "mb-tablebox-collapsible": isCollapsible,
                "mb-tablebox-collapsed": isCollapsible && !isExpanded,
                "mb-tablebox-expanded": isExpanded,
                "mb-tablebox-fillheight": fillHeight,
                "mb-tablebox-bulkeditor": isBulkEditor,
              },
              className,
            )}
            sx={{
              ".mb-tablebox-header, .mb-tablebox-stats, .mb-tablebox-body": {
                _notFirst: {
                  borderTopWidth: "1px",
                },
              },
              " .mb-tablebox-collapse .mb-tablebox-body": {
                borderTopWidth: "1px",
              },
              ...(isFullWidth && !isFullScreen
                ? {
                    borderTopWidth: 1,
                    marginX: "-24px",
                  }
                : {
                    borderWidth: 1,
                    borderRadius: "8px",
                    overflowY: "hidden",
                  }),
              ...(!isFullHeight || !isFullWidth
                ? {
                    mb: "24px",
                  }
                : {}),
              ...(isFullHeight && (!isCollapsible || isExpanded)
                ? {
                    minHeight:
                      isExpanded && expandedMinHeight
                        ? expandedMinHeight
                        : // always show a little bit of the table
                          "calc(var(--mb-tablebox-header-height) + 100px)",
                  }
                : {}),
              ...(isFullScreen
                ? {
                    position: "fixed",
                    top: px(headerNavHeight + 1)!,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    zIndex: "portal",
                    border: 0,
                    mb: 0,
                  }
                : {}),
              ...(isBulkEditor
                ? {
                    flexGrow: 1,
                    mb: 0,
                    border: 0,
                    boxShadow: "none",
                    '.ag-header-cell[aria-colindex="1"]': {
                      paddingLeft: "16px",
                    },
                    ".ag-header-cell, .ag-cell": {
                      borderRightWidth: "1px",
                      borderRightColor: "gray.200",
                    },
                  }
                : {}),
              ...boxPropsSx,
            }}
            {...boxProps}
          >
            {children}
          </Box>
        </FocusLock>
      </MaybernTableUIProvider>
    </TableBoxContext.Provider>
  );
};

TableBox.displayName = "TableBox";
